import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Box, Button, Container, Grid, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { eventBus, EventTypes } from '@core/pubsub/EventBus';
import { DeleteConfirmationDialogRequireInput, NewPageTitleBar } from '@/components';
import { getMenuData } from '@core/data/menuData.ts';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { DateTime } from 'luxon';
import NewWcnTable, { DataTableColumn, KeyValuePair } from '@/components/datatable/NewWcnTable.tsx';
import MediaPlayerTemplatesEditor, {
    MediaPlayerTemplatesEditorRef
} from '@features/administration/components/templates/MediaPlayerTemplatesEditor.tsx';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import { MediaPlayerTemplate, templateService } from '@/services';
import { DeleteConfirmationDialogRef } from '@/components/dialog/DeleteConfirmationDialogRequireInput.tsx';
import { useUnsavedChangesGuard } from '@core/hooks/useUnsavedChangesGuard.ts';
import { useRegisterDirtyGuard } from '@core/hooks/useRegisterDirtyGuard.ts';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay.tsx';

const MediaPlayerTemplates: FCSecurePage = () => {
    const { t } = useTranslation();
    const { notifyErrorResponse, notifySuccess } = useNotifications();
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const deleteDialogRef = useRef<DeleteConfirmationDialogRef>(null);

    const [templates, setTemplates] = useState<MediaPlayerTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<MediaPlayerTemplate | null>(null);
    const [templateToDelete, setTemplateToDelete] = useState<MediaPlayerTemplate | null>(null);
    const [loading, setLoading] = useState(false);
    const editorRef = useRef<MediaPlayerTemplatesEditorRef>(null);
    const discardedRef = useRef(false);
    const isDirty = useCallback(() => !discardedRef.current && !!editorRef.current?.isDirty, []);
    const editorSectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (selectedTemplate) {
            discardedRef.current = false;
        }
    }, [selectedTemplate]);

    useEffect(() => {
        if (selectedTemplate) {
            editorSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [selectedTemplate]);

    const save = useCallback(async () => {
        const saved = await editorRef.current?.save();
        if (saved) {
            handleCloseEditor(true);
        }
        return !!saved;
    }, []);

    const discard = useCallback(() => {
        handleCloseEditor(false);
    }, []);

    const { guard } = useUnsavedChangesGuard({ isDirty, save });
    useRegisterDirtyGuard({ checkDirty: isDirty, save, discard });

    const loadTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const response = await templateService.loadMediaPlayerTemplates();
            setTemplates(response || []);
            setLastUpdate(DateTime.now());
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    }, []);

    const trySelectTemplate = useCallback(
        (next: MediaPlayerTemplate) => {
            guard(() => setSelectedTemplate(next));
        },
        [guard]
    );

    const handleAddNew = useCallback(() => {
        trySelectTemplate({
            id: '',
            name: '',
            configuration: {
                defaultWelcome: '',
                defaultTitle: '',
                defaultFirstName: '',
                defaultLastName: '',
                alternateLine1: '',
                alternateLine2: '',
                alternateLine3: '',
                alternateLine4: ''
            }
        });
    }, [trySelectTemplate]);

    useEffect(() => {
        const unsubscribeReload = eventBus.on(EventTypes.WIDGETS_RELOAD, loadTemplates);
        loadTemplates();
        return () => {
            unsubscribeReload();
        };
    }, [loadTemplates, handleAddNew]);

    const handleEdit = useCallback(
        (template: MediaPlayerTemplate) => {
            trySelectTemplate(template);
        },
        [trySelectTemplate]
    );

    const handleCloseEditor = useCallback(
        (shouldReload = false) => {
            discardedRef.current = true;
            setSelectedTemplate(null);
            if (shouldReload) {
                loadTemplates();
            }
        },
        [loadTemplates]
    );

    const handleCancelEditor = useCallback(() => {
        guard(() => handleCloseEditor(false));
    }, [guard, handleCloseEditor]);

    const handleAskDelete = useCallback((template: MediaPlayerTemplate) => {
        setTemplateToDelete(template);
        deleteDialogRef.current?.openDialog();
    }, []);

    const handleDelete = useCallback(async () => {
        if (!templateToDelete?.id) return;

        setLoading(true);

        try {
            await templateService.deleteMediaPlayerTemplate(templateToDelete.id);
            notifySuccess();
            setTemplateToDelete(null);
            handleCloseEditor(true);
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    }, [templateToDelete]);

    const handleCancelDelete = useCallback(() => {
        setTemplateToDelete(null);
    }, []);

    // ✅ Define table columns
    const tableColumns: DataTableColumn<MediaPlayerTemplate>[] = useMemo(
        () => [
            {
                text: t('name'),
                value: 'name',
                width: 200
            },
            {
                text: t('configuration'),
                value: 'configuration',
                sortable: false,
                multiLine: true,
                width: 400,
                multiLineValue: (template): KeyValuePair[] =>
                    Object.entries(template.configuration).map(([key, value]) => ({
                        key,
                        label: key,
                        value: value || '-'
                    }))
            },
            {
                text: t('actions'),
                value: 'actions',
                width: 100,
                align: 'left',
                sortable: false,
                isAction: true,
                render: (template) => {
                    const isEditing = selectedTemplate?.id === template.id;
                    return (
                        <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap' }}>
                            <IconButton
                                size="small"
                                onClick={() => handleEdit(template)}
                                title={t('edit')}
                                className={isEditing ? 'selected' : undefined}
                            >
                                <i className="bi bi-pencil" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleAskDelete(template)} title={t('delete')}>
                                <i className="bi bi-trash3" />
                            </IconButton>
                        </Box>
                    );
                }
            }
        ],
        [t, selectedTemplate, handleEdit, handleAskDelete]
    );

    const handleRefresh = useCallback(async () => {
        eventBus.emit(EventTypes.WIDGETS_RELOAD);
    }, []);

    useRegisterRefresh(handleRefresh);

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="bi bi-chat-left-dots" />}
                title={t('mediaPlayerTemplates')}
                menus={getMenuData()}
                iconSize="20px"
                currentRoute={{ path: '/manage-organisation/mediaPlayerTemplates' }}
                t={t}
                lastUpdate={lastUpdate}
            />
            <Box className="pagetitlebar-actions">
                <Button
                    variant="text"
                    onClick={handleAddNew}
                    className="pagetitlebar-action-btn pagetitlebar-action-btn--default"
                >
                    <Box className="pagetitlebar-action-btn-icon">
                        <i className="bi bi-plus-lg" style={{ fontSize: '16px', color: 'inherit', lineHeight: 1 }} />
                    </Box>
                    <span>{t('addMediaPlayerConfiguration')}</span>
                </Button>
            </Box>

            <Container maxWidth={false} className="page-template-container">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <NewWcnTable
                            columns={tableColumns}
                            data={templates}
                            loading={loading}
                            noDataMessage={t('noData')}
                            size="small"
                        />
                    </Grid>
                    {selectedTemplate && (
                        <Grid item xs={12} ref={editorSectionRef}>
                            <MediaPlayerTemplatesEditor
                                ref={editorRef}
                                template={selectedTemplate}
                                onSave={() => handleCloseEditor(true)}
                                onCancel={handleCancelEditor}
                                onDelete={handleAskDelete}
                            />
                        </Grid>
                    )}
                </Grid>

                <DeleteConfirmationDialogRequireInput
                    dialogRef={deleteDialogRef}
                    heading={t('deleteWithNameHeading', { item: templateToDelete?.name || '' })}
                    message={t('deleteMessage', { item: t('template') })}
                    cancelText={t('keepConfiguration')}
                    onConfirm={handleDelete}
                    expectedInputText={templateToDelete?.name || ''}
                    onClose={handleCancelDelete}
                />
            </Container>

            <LoadingOverlay open={loading} />
        </Box>
    );
};

export default MediaPlayerTemplates;
